import { useId, useRef, type KeyboardEvent, type ClipboardEvent } from "react";

import { Button, Cell, Kbd, Spacer, Table, Th, Toolbar } from "@/components/kit";
import type { FieldDef, FormOption, SlotOptions } from "@/lib/api/types";
import { applyPaste, blankRow, canAddColumn, connectionColumns, type GridRow } from "@/lib/grid";

import styles from "./EntryGrid.module.css";

/**
 * A start list, entered the way a start list actually arrives.
 *
 * The screen this replaces asked for one control at a time, mouse-driven, and a
 * twenty-runner card took twenty trips to a dropdown. That was the single
 * clearest failure in the whole surface: the most common task in the tool had
 * the least support in it.
 *
 * So this is a grid. Tab moves across, Enter moves down the column the way it
 * does in a spreadsheet, and a block pasted out of one lands where the cursor
 * is — because the card almost always exists in a spreadsheet first, and
 * retyping something you already have is not data entry, it is transcription.
 *
 * Entrants are typed rather than picked. A native datalist gives completion
 * without a popup to manage, keeps the keyboard working exactly as it does
 * everywhere else, and lets a name be pasted in as text. Names resolve to codes
 * on submit, so an unmatched name is a validation error rather than a silent
 * blank.
 *
 * Connections are columns too, and that is not a nicety: a runner is a horse
 * with a jockey, a trainer and an owner attached, and the grid had no room for
 * any of them — so a whole racecard could be entered with every runner's
 * connections silently missing. A racecard in a spreadsheet has a jockey column,
 * so the grid has one, and it pastes like everything else.
 */

export function EntryGrid({
  entrants,
  fields,
  rows,
  onChange,
  entrantLabel,
  connections = [],
  extraColumns = {},
  onAddColumn,
}: {
  entrants: FormOption[];
  /** The draw — lane, stall, seed. Declared by the kind of fixture. */
  fields: FieldDef[];
  rows: GridRow[];
  onChange: (rows: GridRow[]) => void;
  entrantLabel: string;
  /** Who attaches to each entrant — a jockey, a trainer, the owners. */
  connections?: SlotOptions[];
  /** Extra columns opened per role, beyond the one each slot starts with. */
  extraColumns?: Record<string, number>;
  onAddColumn?: (role: string) => void;
}) {
  const listId = useId();
  const grid = useRef<HTMLTableSectionElement>(null);

  const connColumns = connectionColumns(connections, extraColumns);
  const columns = ["entrant", ...fields.map((f) => f.key), ...connColumns.map((c) => c.key)];
  /** Where the connection columns start, so keyboard coordinates line up. */
  const connFrom = 1 + fields.length;

  function set(row: number, column: number, value: string) {
    const next = rows.map((r, i) => {
      if (i !== row) return r;
      if (column === 0) return { ...r, entrant: value };
      return { ...r, values: { ...r.values, [columns[column]]: value } };
    });
    // Always one spare row at the bottom, so filling the last one never means
    // reaching for a button before carrying on.
    if (row === next.length - 1 && value !== "") next.push(blankRow());
    onChange(next);
  }

  /** Move to a cell by its coordinates, if it exists. */
  function focusCell(row: number, column: number) {
    const input = grid.current?.querySelector<HTMLInputElement>(
      `[data-row="${row}"][data-col="${column}"]`,
    );
    input?.focus();
    input?.select();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>, row: number, column: number) {
    // Tab is the browser's, and it already does the right thing. These are the
    // ones a spreadsheet adds.
    if (e.key === "Enter" || e.key === "ArrowDown") {
      e.preventDefault();
      focusCell(row + 1, column);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusCell(row - 1, column);
    }
  }

  /**
   * A block out of a spreadsheet, landing where the cursor is.
   *
   * Tab-separated columns, newline-separated rows — what every spreadsheet puts
   * on the clipboard. Rows are added as needed, so pasting twenty runners into
   * an empty grid works without preparing twenty rows first.
   */
  function onPaste(e: ClipboardEvent<HTMLInputElement>, row: number, column: number) {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return; // one value: ordinary typing
    e.preventDefault();
    onChange(applyPaste(rows, row, column, columns, text));
  }

  const filled = rows.filter((r) => r.entrant.trim() !== "").length;

  return (
    // A gapped column rather than a fragment: `Toolbar` carries a bottom margin and
    // no top one, so after a table it sat flush against the last row.
    <div className={styles.grid}>
      <Table>
        <thead>
          <tr>
            <Th>#</Th>
            <Th>{entrantLabel}</Th>
            {fields.map((field) => (
              <Th key={field.key}>{field.label ?? field.key}</Th>
            ))}
            {connColumns.map((column) => (
              <Th key={column.key}>{column.label}</Th>
            ))}
            <Th />
          </tr>
        </thead>
        <tbody ref={grid}>
          {rows.map((row, r) => (
            <tr key={r}>
              <td style={{ color: "var(--ink-3)", fontSize: "var(--t-xs)", width: 32 }}>{r + 1}</td>
              <Cell
          boxed
                data-row={r}
                data-col={0}
                list={listId}
                value={row.entrant}
                placeholder={r === 0 ? `Type or paste a ${entrantLabel.toLowerCase()}` : ""}
                onChange={(e) => set(r, 0, e.target.value)}
                onKeyDown={(e) => onKeyDown(e, r, 0)}
                onPaste={(e) => onPaste(e, r, 0)}
              />
              {fields.map((field, c) => (
                <Cell
          boxed
                  key={field.key}
                  data-row={r}
                  data-col={c + 1}
                  type={
                    field.type === "integer" || field.type === "number" ? "number" : "text"
                  }
                  value={row.values[field.key] ?? ""}
                  onChange={(e) => set(r, c + 1, e.target.value)}
                  onKeyDown={(e) => onKeyDown(e, r, c + 1)}
                  onPaste={(e) => onPaste(e, r, c + 1)}
                />
              ))}
              {connColumns.map((column, c) => (
                <Cell
          boxed
                  key={column.key}
                  data-row={r}
                  data-col={connFrom + c}
                  list={`${listId}-${column.slot.role}`}
                  value={row.values[column.key] ?? ""}
                  onChange={(e) => set(r, connFrom + c, e.target.value)}
                  onKeyDown={(e) => onKeyDown(e, r, connFrom + c)}
                  onPaste={(e) => onPaste(e, r, connFrom + c)}
                />
              ))}
              <td style={{ width: 32 }}>
                {rows.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove row ${r + 1}`}
                    onClick={() => onChange(rows.filter((_, i) => i !== r))}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "var(--ink-3)",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* One list for every entrant cell — the browser does the completion, so
          there is no popup to keep in step with the keyboard. */}
      <datalist id={listId}>
        {entrants.map((option) => (
          <option key={option.code} value={option.name} />
        ))}
      </datalist>
      {/* One list per connection slot, so the jockey column completes jockeys. */}
      {connections.map((slot) => (
        <datalist id={`${listId}-${slot.role}`} key={slot.role}>
          {slot.options.map((option) => (
            <option key={option.code} value={option.name} />
          ))}
        </datalist>
      ))}

      <Toolbar>
        <Button onClick={() => onChange([...rows, blankRow()])}>Add a row</Button>
        <span style={{ fontSize: "var(--t-sm)", color: "var(--ink-3)" }}>
          {filled} in the field
        </span>
        {/* A slot that takes several people gets another column rather than
            another row: a syndicate is still one runner. */}
        {onAddColumn &&
          connections
            .filter((slot) => canAddColumn(slot, extraColumns))
            .map((slot) => (
              <Button key={slot.role} onClick={() => onAddColumn(slot.role)}>
                Another {slot.label.toLowerCase()}
              </Button>
            ))}
        <Spacer />
        <span style={{ fontSize: "var(--t-xs)", color: "var(--ink-3)" }}>
          <Kbd>Tab</Kbd> across · <Kbd>↵</Kbd> down · <Kbd>⌘</Kbd>
          <Kbd>V</Kbd> pastes a block
        </span>
      </Toolbar>
    </div>
  );
}
